import {
    PERIOD_TO_NAME_MAP,
    UNKNOWN_ALIAS,
    UNKNOWN_COUNTRY,
} from "app/pages/sales-intelligence/sub-modules/signals/constants/common";
import type AdNetworkSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalFilters";
import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import type NewsSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalFilters";
import type TechnologySignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalFilters";
import type TrafficChangesModel from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesModel";
import type TrafficMarketModel from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficMarketModel";
import type TrafficSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalFilters";
import TrafficSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalSubType";
import { isTrafficChangesModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficChangesModel";
import { isTrafficMarketModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficMarketModel";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import { buildTechnologiesTrackingValue } from "app/pages/sales-intelligence/sub-modules/signals/services/tracking/signals-filters/helpers";
import type { AdNetwork } from "app/pages/workspace/sales/sub-modules/feed/types/adNetwork";
import type { ICountryObject } from "app/services/CountryService";
import type { CustomAttributes } from "app/services/track/track";

import {
    PERIOD_TO_TRACKING_VALUE_MAP,
    SIGNAL_TRAFFIC_SYB_TYPE_TO_TRACKING_VALUE,
    TECHNOLOGY_SIGNAL_ALIAS_TO_TRACKING_VALUE,
    TRACKING_FILTERS_ATTRIBUTES,
} from "./const";
import type IntentSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/intent/IntentSignalFilters";

function joinArrayOrFallback(arr?: ReadonlyArray<string | number> | null): string {
    return arr?.join(",") || "";
}

function getCountryName(countryId: ICountryObject["id"]): string {
    try {
        return signalsService.getSignalCountryById(countryId).text;
    } catch (e) {
        return UNKNOWN_COUNTRY;
    }
}

function getTrafficSignalTrackingValue(filters: TrafficSignalFilters): string {
    if (isTrafficChangesModel(filters.trafficChanges)) {
        return PERIOD_TO_NAME_MAP[filters.trafficChanges.period];
    }

    return "unknown-period";
}

function getIntentSignalTrackingValue(filters: IntentSignalFilters): string {
    return filters.topics.length.toString();
}

function getIntentSignalTrackingTopicValue(filters: IntentSignalFilters): string {
    return filters.topics.join(",").replace("/", "~");
}

function getSignalTrackingEditModeTopicValue<F extends SignalFilters>(signal: Signal<F>) {
    switch (signal.signalType) {
        case SignalType.Intent:
            return getIntentSignalTrackingTopicValue(signal.filters as unknown as IntentSignalFilters);
        default:
            return getSignalTrackingValues(signal);
    }
}

export function getSignalTrackingValues<F extends SignalFilters>(signal: Signal<F>) {
    switch (signal.signalType) {
        case SignalType.AdNetwork:
            return (signal.filters as unknown as AdNetworkSignalFilters).adNetworkIds;
        case SignalType.Technology:
            return buildTechnologiesTrackingValue(signal.filters);
        case SignalType.Traffic:
            return getTrafficSignalTrackingValue(signal.filters);
        case SignalType.Ecommerce:
            return signal.signalSubType;
        case SignalType.Intent:
            return getIntentSignalTrackingValue(signal.filters as unknown as IntentSignalFilters);
        case SignalType.News:
            return (signal.filters as unknown as NewsSignalFilters).categories;
    }
}

function getTrafficSignalTrackingValues<F extends SignalFilters>(alias: string, signal: Signal<F>) {
    switch (signal.signalSubType) {
        case TrafficSignalSubType.Engagement:
            return "change by engagement metric";
        default:
            return alias;
    }
}

export function getSignalCreateEventName<F extends SignalFilters>(signal: Signal<F>): string {
    const { alias = UNKNOWN_ALIAS } = signalsService.getSignalTemplate(signal) ?? {};

    switch (signal.signalType) {
        case SignalType.AdNetwork:
        case SignalType.Technology:
            return `${alias}/added successfully`;
        case SignalType.Traffic:
            return `${getTrafficSignalTrackingValues(alias, signal)}/added successfully`;
        case SignalType.Ecommerce:
        case SignalType.News:
        case SignalType.Intent:
            const filterValue = getSignalTrackingValues(signal);

            return `${filterValue}/added successfully`;
        default:
            return "unknown-signal-type";
    }
}

function getCustomAttributesFromAdNetworkSignalFilters(
    signal: Signal<AdNetworkSignalFilters>,
    signalsDescription: Readonly<SignalsDescription> | null,
): CustomAttributes {
    const { adNetworkIds, action } = signal.filters as AdNetworkSignalFilters;
    const adNetworkIdSet = new Set(adNetworkIds);
    const adNetworkNames = signalsDescription?.adNetworks.reduce((names, ad: AdNetwork) => {
        if (adNetworkIdSet.has(ad.id)) {
            names.push(ad.name);
        }

        return names;
    }, [] as string[]);

    return {
        [TRACKING_FILTERS_ATTRIBUTES.AD_NETWORKS]: joinArrayOrFallback(adNetworkNames),
        [TRACKING_FILTERS_ATTRIBUTES.AD_NETWORK_OPTION]: action,
    };
}

function getCustomAttributesFromTechnologySignalFilters(signal: Signal<TechnologySignalFilters>): CustomAttributes {
    const { technologySubCategories, technologyCategories, technologies } = signal.filters;
    const { alias } = signalsService.getSignalTemplate(signal) ?? {};

    const techOption = alias ? TECHNOLOGY_SIGNAL_ALIAS_TO_TRACKING_VALUE[alias] : UNKNOWN_ALIAS;

    return {
        [TRACKING_FILTERS_ATTRIBUTES.TECH_OPTION]: techOption,

        [TRACKING_FILTERS_ATTRIBUTES.TECH_TECHNOLOGIES]: joinArrayOrFallback(technologies),
        [TRACKING_FILTERS_ATTRIBUTES.TECH_TECHNOLOGY_CATEGORIES]: joinArrayOrFallback(technologyCategories),
        [TRACKING_FILTERS_ATTRIBUTES.TECH_TECHNOLOGY_SUB_CATEGORIES]: joinArrayOrFallback(technologySubCategories),
    };
}

function getTrafficChangesCustomAttributes({
    country,
    metric,
    direction,
    period,
    value,
}: TrafficChangesModel): CustomAttributes {
    const countryName = getCountryName(country);

    return {
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_METRIC]: metric,
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_COUNTRY]: countryName,
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_DIRECTION]: direction,
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_PERIOD]: PERIOD_TO_TRACKING_VALUE_MAP[period],
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_VALUE]: value,
    };
}

function getNewMarketsCustomAttributes({ thresholdValue, metric, countries }: TrafficMarketModel): CustomAttributes {
    const countriesNames = joinArrayOrFallback(countries.map(getCountryName));

    return {
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_METRIC]: metric,
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_VALUE]: thresholdValue,
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_COUNTRY]: countriesNames,
    };
}

function getCustomAttributesFromTrafficSignalFilters({
    signalSubType,
    filters,
}: Signal<TrafficSignalFilters>): CustomAttributes {
    const customAttributes = {
        [TRACKING_FILTERS_ATTRIBUTES.TRAFFIC_OPTION]:
            SIGNAL_TRAFFIC_SYB_TYPE_TO_TRACKING_VALUE[signalSubType as TrafficSignalSubType],
    };

    if (isTrafficChangesModel(filters.trafficChanges)) {
        return {
            ...customAttributes,
            ...getTrafficChangesCustomAttributes(filters.trafficChanges),
        };
    }

    if (isTrafficMarketModel(filters.newMarkets)) {
        return {
            ...customAttributes,
            ...getNewMarketsCustomAttributes(filters.newMarkets),
        };
    }

    return {};
}

function getCustomAttributesFromNewsSignalFilters(signal: Signal<NewsSignalFilters>): CustomAttributes {
    const { categories } = signal.filters;

    return {
        [TRACKING_FILTERS_ATTRIBUTES.NEWS_CATEGORIES]: joinArrayOrFallback(categories),
    };
}

export default function getCustomAttributesFromSignalFilters<F extends SignalFilters>(
    signal: Signal<F>,
    signalsDescription: Readonly<SignalsDescription> | null,
): CustomAttributes {
    switch (signal.signalType) {
        case SignalType.AdNetwork:
            return getCustomAttributesFromAdNetworkSignalFilters(signal, signalsDescription);
        case SignalType.Technology:
            return getCustomAttributesFromTechnologySignalFilters(signal);
        case SignalType.Traffic:
            return getCustomAttributesFromTrafficSignalFilters(signal);
        case SignalType.Ecommerce:
            return {};
        case SignalType.News:
            return getCustomAttributesFromNewsSignalFilters(signal);
        case SignalType.Intent:
        default:
            return {};
    }
}

function getDefaultEditEventName<F extends SignalFilters>(updatedSignal: Signal<F>, initialSignal: Signal<F>): string {
    const formattedSignalType = updatedSignal.signalType.toLowerCase();
    const initialValue = getSignalTrackingEditModeTopicValue(initialSignal);
    const updatedValue = getSignalTrackingValues(updatedSignal);

    return `${initialValue}/${updatedValue}/${formattedSignalType} signal edited successfully`;
}

function getTrafficEditEventName<F extends TrafficSignalFilters>(
    updatedSignal: Signal<F>,
    initialSignal: Signal<F>,
): string {
    const {
        signalSubType,
        filters: { trafficChanges },
    } = updatedSignal;
    const { metric } = trafficChanges ?? {};

    switch (signalSubType) {
        case TrafficSignalSubType.Engagement:
            return `edit engagement signal/${String(metric)}`;

        default:
            return getDefaultEditEventName(updatedSignal, initialSignal);
    }
}

export function getSignalEditEventName<F extends SignalFilters>(
    updatedSignal: Signal<F>,
    initialSignal: Signal<F>,
): string {
    const { signalType } = updatedSignal;

    switch (signalType) {
        case SignalType.Traffic:
            return getTrafficEditEventName(updatedSignal, initialSignal);

        default:
            return getDefaultEditEventName(updatedSignal, initialSignal);
    }
}
